import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { projectKeys } from './useProjects';
import { toast } from 'sonner';

// Types
interface ImportResult {
  projectId: string;
  projectName: string;
  tasksImported: number;
  warnings?: string[];
}

export interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
  confidence: number;
}

interface ImportMsProjectResponse {
  project: ImportResult;
}

interface ImportCsvResponse {
  project?: ImportResult;
  mappings?: ColumnMapping[];
  requiresMapping: boolean;
}

// Import MS Project (.xml)
export function useImportMsProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post<ImportMsProjectResponse>(
        '/projects/import/ms-project',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// Import CSV/Excel
export function useImportCsv() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post<ImportCsvResponse>(
        '/projects/import/csv',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.project) {
        queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      }
    },
  });
}

// Export project
export function useExportProject() {
  const downloadFile = async (projectId: string, format: 'csv' | 'excel' | 'ms-project') => {
    try {
      const endpointMap: Record<string, string> = {
        csv: 'csv',
        excel: 'xlsx',
        'ms-project': 'ms-project-xml',
      };
      const endpoint = endpointMap[format] || format;

      const response = await api.get<Blob>(`/projects/${projectId}/export/${endpoint}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const extensionMap: Record<string, string> = {
        csv: 'csv',
        excel: 'xlsx',
        'ms-project': 'xml',
      };

      const link = document.createElement('a');
      link.href = url;
      link.download = `project-${projectId}.${extensionMap[format]}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      toast.error(error?.message || 'Export failed');
    }
  };

  return { downloadFile };
}
